import { allowedMime } from "@api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import WarningIcon from "@mui/icons-material/WarningAmber";
import { Alert, Button, IconButton, LinearProgress, Stack, Typography } from "@mui/material";
import { TypoTooltip } from "@styleguide";
import { type ChangeEvent, useRef, useState } from "react";
import { type Control, useFieldArray } from "react-hook-form";
import type { IDocumentsActionsProgress } from "../logics/documents";
import type { TModalSocialOfferFormResolver } from "../logics/resolver";

interface IModalSocialOfferFormFieldDocumentsProps {
  control: Control<TModalSocialOfferFormResolver>;
  loading: boolean;
  documentsProgress: IDocumentsActionsProgress | null;
}

const fileInputAccept = (allowedMime ?? []).join(",");

export const ModalSocialOfferFormFieldDocuments = ({
  control,
  loading,
  documentsProgress,
}: IModalSocialOfferFormFieldDocumentsProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [warning, setWarning] = useState(false);
  const { fields: documents, append, remove } = useFieldArray({ control, name: "documents" });

  const handleOpenFileInput = () => {
    inputRef.current?.click();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setWarning(documents.length + files.length > 5);
      const fields: TModalSocialOfferFormResolver["documents"] = [];
      for (let i = 0; i < 5 - documents.length && i < files.length; i++) {
        if (files[i].size <= 5242880) {
          fields.push({ file: files[i] });
        } else {
          setWarning(true);
        }
      }
      append(fields);
    }
  };

  const handleRemoveFile = (index: number) => {
    setWarning(false);
    remove(index);
  };

  return (
    <>
      <Alert color="warning" icon={<WarningIcon />} {...(!warning && { sx: { display: "none" } })}>
        Vous ne pouvez importer que 5 fichiers maximum
        <br />
        La taille maximal d'un fichier est de 5Mo
      </Alert>
      {documentsProgress && (
        <LinearProgress
          variant="determinate"
          value={(documentsProgress.currentStep / documentsProgress.totalSteps) * 100}
        />
      )}
      <Stack
        direction="row"
        sx={{
          alignItems: "start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <input
          type="file"
          style={{ display: "none" }}
          onChange={handleChange}
          accept={fileInputAccept}
          multiple
          ref={inputRef}
        />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenFileInput}
          size="small"
          sx={{ flex: "0 0 auto", textTransform: "unset" }}
          disabled={Boolean(documentsProgress) || loading || documents.length >= 5}
        >
          <Typography variant="body1SemiBold">Ajouter un document</Typography>
        </Button>
        <Stack
          sx={{
            alignItems: "end",
            overflow: "hidden",
          }}
        >
          {documents.map((document, index) => (
            <Stack
              key={document.id}
              direction="row"
              sx={{
                alignItems: "center",
                gap: 0.5,
                maxWidth: "100%",
              }}
            >
              <TypoTooltip variant="caption" noWrap>
                {document.info?.name ?? document.file?.name}
              </TypoTooltip>
              <IconButton
                size="small"
                sx={{ flex: "0 0 auto" }}
                onClick={() => handleRemoveFile(index)}
                disabled={Boolean(documentsProgress) || loading}
              >
                <DeleteIcon color="primary" fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </>
  );
};
